"use client"

import { useState, useEffect, useCallback } from "react"
import { FaEye } from "react-icons/fa"
import Select from "../inventory/modules/Select/Select"
import RequestDetailModal from "./modal/RequestDetailModal"
import SupportRequestDetailModal from "./modal/SupportRequestDetailModal"
import Badge from "./modules/Badge/Badge"
import SearchBar from "../shared-modules/SearchBar"
import Spinner from "../spinner"
import Modal from "../shared-modules/Modal"
import {
  getTicketRequests,
  approveTicketRequest,
  getDeviceTypes,
  fetchByUrl,
} from "../../services/inventory/detail"
import styles from "../../styles/inventory/technicalSupport.module.css"
import { parseDateSafe } from "../../utils/dateFormatters"

const PagingButton = ({ onClick, disabled, loading, children, ariaLabel }) => (
  <button
    className={styles.paging_button}
    onClick={onClick}
    disabled={disabled || loading}
    aria-label={ariaLabel}
    type="button"
  >
    {children}
  </button>
)

const StatusBadgeInTable = ({ currentStatus }) => {
  const variantMap = {
    pending: "warning",
    canceled: "danger",
    forwarded: "success",
  }
  const variant = variantMap[currentStatus] || "default"
  return <Badge variant={variant}>{currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1)}</Badge>
}

const TicketRequestRow = ({ request, onRowClick, onActionButtonClick }) => (
  <div className={styles.row} onClick={() => onRowClick(request)}>
    <div className={styles.serial_number}>{request.id ? request.id.split("-")[0] : request.requestId}</div>
    <div className={styles.status_cell}>
      <StatusBadgeInTable currentStatus={request.status} />
    </div>
    <div className={styles.device_type}>{request.deviceType}</div>
    <div className={styles.issue_description}>{request.issue}</div>
    <div className={styles.ticket_date}>{request.requestDate}</div>
    <div className={styles.ticket_location}>{request.location}</div>
    <div className={styles.ticket_actions}>
      <button
        className={styles.view_history_btn}
        onClick={(e) => {
          e.stopPropagation()
          onActionButtonClick(request)
        }}
      >
        <FaEye className={styles.button_icon} /> View Events
      </button>
    </div>
  </div>
)

const TicketRequestSection = ({ refreshTrigger = 0 }) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [locationFilter, setLocationFilter] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [deviceTypeFilter, setDeviceTypeFilter] = useState("")
  const [requests, setRequests] = useState([])
  const [deviceTypes, setDeviceTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [paginationLoading, setPaginationLoading] = useState(false)
  const [error, setError] = useState(null)
  const [isAdmin, setIsAdmin] = useState(false)

  const [selectedSupportRequest, setSelectedSupportRequest] = useState(null)
  const [selectedRequestRequest, setSelectedRequestRequest] = useState(null)
  const [isForwarding, setIsForwarding] = useState(false)
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)
  const [successMessage, setSuccessMessage] = useState("")

  const [meta, setMeta] = useState(null)

  useEffect(() => {
    try {
      const accountStr = sessionStorage.getItem("inventoryActiveAccount")
      if (accountStr) {
        const account = JSON.parse(accountStr)
        setIsAdmin(account?.type?.toLowerCase().trim() === "admin")
      }
    } catch (err) {
      console.error("Failed to parse account info", err)
    }
  }, [])

  const mapServerItem = (item) => ({
    id: item.id,
    requestId: item.request_number || "N/A",
    deviceType: item.device_type_name || "N/A",
    issue: item.issue_description || "N/A",
    requestDate: item.created_at
      ? parseDateSafe(item.created_at)?.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "N/A",
    location: item.location_name || "N/A",
    status: item.status || "pending",
    patientName: item.patient_name || "Unknown Patient",
    gatewayId: item.IMEI,
    tenovi_ticket_id: item.tenovi_ticket_id || null,
    original: item,
  })

  const fetchTickets = useCallback(
    async (pageOrUrl = 1) => {

      const isPaginationCall = typeof pageOrUrl === "string" || (typeof pageOrUrl === "number" && pageOrUrl !== 1)
      try {
        if (isPaginationCall) {
          setPaginationLoading(true)
        } else {
          setLoading(true)
        }
        setError(null)

        let response

        if (typeof pageOrUrl === "string") {
          response = await fetchByUrl(pageOrUrl)
        } else {

          const params = {}
          if (!isAdmin && locationFilter) params.location_id = locationFilter

          params.status = statusFilter || "not_forwarded"
          if (typeof pageOrUrl === "number" && pageOrUrl > 1) params.page = pageOrUrl

          try {

            response = await getTicketRequests(params)
          } catch (e) {

            response = await getTicketRequests(!isAdmin && locationFilter ? locationFilter : null, params.status)
          }
        }

        const payload = response?.success ? response.data : response
        const results = payload?.results ?? payload?.data ?? payload ?? []
        const metaObj = payload?.meta ?? payload?.data?.meta ?? null

        const mapped = Array.isArray(results) ? results.map(mapServerItem) : []
        setRequests(mapped)
        setMeta(metaObj)
      } catch (err) {
        console.error("Error fetching ticket requests:", err)
        setError(err?.message || "Failed to load ticket requests")
      } finally {
        setLoading(false)
        setPaginationLoading(false)
      }
    },
    [isAdmin, locationFilter, statusFilter],
  )

  useEffect(() => {
    fetchTickets(1)
  }, [fetchTickets, refreshTrigger])

  useEffect(() => {
    fetchTickets(1)
  }, [isAdmin, locationFilter, statusFilter, fetchTickets])

  useEffect(() => {
    if (!isSuccessModalOpen) return
    const timer = setTimeout(() => setIsSuccessModalOpen(false), 3000)
    return () => clearTimeout(timer)
  }, [isSuccessModalOpen])

  const handleForwardToTenovi = async (ticket) => {
    if (!ticket || ticket.status === "forwarded") return
    setIsForwarding(true)
    try {
      const res = await approveTicketRequest(ticket.id)
      if (res.success) {
        setRequests((prev) => prev.map((r) => (r.id === ticket.id ? { ...r, status: "forwarded" } : r)))
        setSuccessMessage("Ticket forwarded successfully!")
        setIsSuccessModalOpen(true)
        setSelectedSupportRequest(null)
      } else {
        setError(res.message || "Failed to forward ticket")
      }
    } catch (err) {
      console.error(err)
      setError("Error forwarding ticket")
    } finally {
      setIsForwarding(false)
    }
  }

  const handleStatusChange = (newStatus, ticket) => {
    setRequests((prev) => prev.map((r) => (r.id === ticket.id ? { ...r, status: newStatus } : r)))
    setSelectedSupportRequest((prev) => (prev?.id === ticket.id ? { ...prev, status: newStatus } : prev))
  }

  const metaGetter = (m) => (keys) => {
    if (!m) return null
    for (const k of keys) if (k in m) return m[k]
    return null
  }
  const metaGetterFn = metaGetter(meta)
  const metaPrev = metaGetterFn(["previous", "prev", "previous_page", "prev_page", "previous_url"])
  const metaNext = metaGetterFn(["next", "next_page", "next_url"])
  const metaCurrentPage = metaGetterFn(["current_page", "page", "current", "page_number", "page_no"])
  const metaTotalPages = metaGetterFn(["total_pages", "total_pages_count", "totalPages"])
  const metaTotalItems = metaGetterFn(["total_items", "count", "total"])

  const handlePageClick = (which) => {
    if (!meta) return
    if (which === "prev") {
      if (!metaPrev) return

      fetchTickets(metaPrev)
      return
    }
    if (which === "next") {
      if (!metaNext) return
      fetchTickets(metaNext)
      return
    }
  }

  const filteredRequests = requests.filter((r) => {
    const q = searchTerm?.toLowerCase() || ""
    const matchesSearch =
      (!q && true) ||
      (r.id && r.id.toLowerCase().includes(q)) ||
      r.deviceType.toLowerCase().includes(q) ||
      r.issue.toLowerCase().includes(q)

    const matchesLocation = isAdmin ? true : !locationFilter || r.location === locationFilter
    const matchesStatus = !statusFilter || r.status === statusFilter
    const matchesDeviceType = !deviceTypeFilter || r.deviceType === deviceTypeFilter

    return matchesSearch && matchesLocation && matchesStatus && matchesDeviceType
  })

  useEffect(() => {
    const loadDeviceTypes = async () => {
      try {
        const devices = await getDeviceTypes()
        if (devices) setDeviceTypes(devices)
      } catch (err) {
        console.error("Error loading device types:", err)
      }
    }
    loadDeviceTypes()
  }, [])

  return (
    <div className={styles.technical_support_section}>
      <div className={styles.search_section}>
        <SearchBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onSearch={() => {}}
          placeholder="Search here"
          maxWidth="300px"
        />

        <div className={styles.filter_container}>
          <Select
            options={[{ value: "", label: "All Devices" }, ...deviceTypes.map((d) => ({ value: d.name, label: d.name }))]}
            value={deviceTypeFilter}
            onChange={setDeviceTypeFilter}
            size="small"
            width="180px"
          />
        </div>

        <div className={styles.filter_container}>
          <Select
            options={[
              { value: "", label: "All Statuses" },
              { value: "pending", label: "Pending" },
              { value: "canceled", label: "Canceled" },
              { value: "forwarded", label: "Forwarded" },
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
            size="small"
            width="180px"
          />
        </div>
      </div>

      <div className={styles.tickets_container}>
        <div className={styles.header}>
          <div className={styles.header_cell}>Request ID</div>
          <div className={styles.header_cell}>Status</div>
          <div className={styles.header_cell}>Device Type</div>
          <div className={styles.header_cell}>Issue Description</div>
          <div className={styles.header_cell}>Request Date</div>
          <div className={styles.header_cell}>Location</div>
          <div className={styles.header_cell}>Actions</div>
        </div>

        {loading ? (
          <div className={styles.no_results}>
            <Spinner />
          </div>
        ) : filteredRequests.length > 0 ? (
          filteredRequests.map((request) => (
            <TicketRequestRow
              key={request.id}
              request={request}
              onRowClick={(r) => setSelectedSupportRequest(r)}
              onActionButtonClick={(r) => setSelectedRequestRequest(r)}
            />
          ))
        ) : (
          <div className={styles.no_results}>
            <p>No record found</p>
          </div>
        )}
      </div>

      {}
      {meta && (
        <div className={styles.pagination_footer}>
          <div className={styles.pagination_left}>
            <div className={styles.smallMeta}>Total: <b>{metaTotalItems ?? "-"}</b></div>
          </div>
          <div className={styles.pagination_controls}>
            <PagingButton onClick={() => handlePageClick("prev")} disabled={!metaPrev} loading={paginationLoading} ariaLabel="Previous page">
              Prev
            </PagingButton>

            <div className={styles.pagination_info}>
              Page <b>{metaCurrentPage ?? "-"}</b> of <b>{metaTotalPages ?? "-"}</b>
            </div>

            <PagingButton onClick={() => handlePageClick("next")} disabled={!metaNext} loading={paginationLoading} ariaLabel="Next page">
              Next
            </PagingButton>
          </div>
        </div>
      )}

      {selectedSupportRequest && (
        <SupportRequestDetailModal
          key={selectedSupportRequest.id}
          isOpen={!!selectedSupportRequest}
          onClose={() => setSelectedSupportRequest(null)}
          ticket={selectedSupportRequest}
          ticketType="ticket-request"
          onStatusChange={handleStatusChange}
          footerButtons={[
            { label: "Cancel", variant: "secondary", onClick: () => setSelectedSupportRequest(null) },
            {
              label: isForwarding ? "Forwarding..." : "Forward to Tenovi",
              variant: "primary",
              onClick: () => handleForwardToTenovi(selectedSupportRequest),
              disabled: isForwarding || selectedSupportRequest?.status === "forwarded",
            },
          ]}
        />
      )}

      {selectedRequestRequest && (
        <RequestDetailModal
          key={selectedRequestRequest.id}
          isOpen={!!selectedRequestRequest}
          onClose={() => setSelectedRequestRequest(null)}
          ticket={selectedRequestRequest}
          currentPatient={{
            user: {
              first_name: selectedRequestRequest.patientName?.split(" ")[0] || "",
              last_name: selectedRequestRequest.patientName?.split(" ")[1] || "",
            },
          }}
          requestData={{ description: selectedRequestRequest?.issue || "" }}
        />
      )}

      {isSuccessModalOpen && (
        <Modal isOpen={isSuccessModalOpen} onClose={() => setIsSuccessModalOpen(false)} title={"Success"} width="500px">
          <p>{successMessage}</p>
        </Modal>
      )}
    </div>
  )
}

export default TicketRequestSection